// apps/admin/src/features/settings/useSettings.ts
//
// Phase 8.3 Wave 6 Task 41 — shared hook for the System Settings tabs.
//
// vg-binding: CONTEXT:decisions=P8.D-100,P8.D-101,P8.D-120,P8.D-170
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Encapsulates the load → edit → save lifecycle that every settings tab
// shares:
//   1. Fetch the namespace envelope on mount (GET /admin/settings/:ns).
//      The hook tracks the server's `version` integer so PATCH callers can
//      send `expected_version` and detect 409 VERSION_CONFLICT optimistic
//      lock failures.
//   2. Expose a `draft` mirror that callers mutate via `setDraft(...)`. The
//      hook automatically flips `dirty=true` once the JSON-stringified
//      shapes diverge (deep equality is overkill for the namespace
//      envelopes — they're flat-ish records).
//   3. `save()` PATCHes the diff, surfaces toast on 2xx, opens the
//      `ConflictDiffModal` state on 409, and re-throws other ApiErrors so
//      the caller can render inline field errors via `error.field_errors`.
//   4. `reload()` re-fetches the namespace (used by the conflict modal's
//      Re-load button + the Discard button when the user wants the server
//      copy back).
//
// The hook is intentionally generic over the namespace value shape so
// tasks 42/43 can reuse it for the remaining four namespaces.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useToast } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { ApiError } from '../../lib/apiClient.js';
import {
  getSettings,
  isVersionConflict,
  patchSettings,
  type SettingsEnvelope,
  type SettingsNamespace,
} from '../../api/systemSettings.api.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ConflictState<TValues> {
  open: boolean;
  /** Draft the user tried to save (their "yours" side). */
  yourValues: TValues;
  /** Server's current values (from `error.details.current_values`). */
  serverValues: TValues;
  currentVersion: number;
  expectedVersion: number;
}

export interface UseSettingsResult<TValues> {
  /** True while the initial GET is in flight (no envelope yet). */
  loading: boolean;
  /** True while a PATCH is in flight. */
  saving: boolean;
  /** Server envelope — null until the GET resolves. */
  envelope: SettingsEnvelope<TValues> | null;
  /** Caller-mutated draft mirror. Equal to `envelope.values` until edited. */
  draft: TValues | null;
  /** True iff `draft` diverges from `envelope.values` (JSON compare). */
  dirty: boolean;
  /** Last load/save error (network/HTTP); null on success. */
  error: Error | null;
  /** VERSION_CONFLICT modal state (open + diff sides). */
  conflict: ConflictState<TValues> | null;
  /** Caller mutator — replaces draft (or applies a partial via callback). */
  setDraft: (
    updater: TValues | ((prev: TValues) => TValues),
  ) => void;
  /** Re-fetch the namespace (used by Discard / 409 Re-load). */
  reload: () => Promise<void>;
  /** PATCH with optimistic lock; toasts on success, opens conflict on 409. */
  save: () => Promise<void>;
  /** Resend with `current_version` (SA-only force-overwrite path). */
  forceOverwrite: () => Promise<void>;
  /** Close conflict modal without action. */
  dismissConflict: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function diffPatch<T>(
  before: T,
  after: T,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const beforeObj = (before ?? {}) as Record<string, unknown>;
  const afterObj = (after ?? {}) as Record<string, unknown>;
  const keys = new Set<string>([
    ...Object.keys(beforeObj),
    ...Object.keys(afterObj),
  ]);
  for (const k of keys) {
    if (!isEqual(beforeObj[k], afterObj[k])) {
      out[k] = afterObj[k];
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * `useSettings(namespace)` — load + draft + save state machine for a single
 * system_settings namespace. Returns the canonical envelope + a caller-owned
 * draft + helpers to save / reload / handle 409 conflicts.
 */
export function useSettings<TValues = Record<string, unknown>>(
  namespace: SettingsNamespace,
): UseSettingsResult<TValues> {
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  const [envelope, setEnvelope] =
    useState<SettingsEnvelope<TValues> | null>(null);
  const [draft, setDraftState] = useState<TValues | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [conflict, setConflict] =
    useState<ConflictState<TValues> | null>(null);

  // Track whether the consumer has actively unmounted so the in-flight
  // fetch doesn't update state after teardown (StrictMode double-mount safe).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSettings<TValues>(namespace);
      if (!mountedRef.current) return;
      setEnvelope(res);
      setDraftState(res.values);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      const msg =
        err instanceof ApiError
          ? err.message
          : t('useSettings.loadFailFallback');
      toast({
        title: t('useSettings.loadFailTitle'),
        description: msg,
        variant: 'error',
      });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [namespace, toast, t]);

  // Mount + namespace change → re-fetch.
  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => {
    if (!envelope || !draft) return false;
    return !isEqual(envelope.values, draft);
  }, [envelope, draft]);

  const setDraft = useCallback(
    (updater: TValues | ((prev: TValues) => TValues)): void => {
      setDraftState((prev) => {
        if (prev == null) return prev;
        return typeof updater === 'function'
          ? (updater as (p: TValues) => TValues)(prev)
          : updater;
      });
    },
    [],
  );

  const reload = useCallback(async (): Promise<void> => {
    await load();
  }, [load]);

  // Internal PATCH executor — used by both `save()` and `forceOverwrite()`.
  const runPatch = useCallback(
    async (versionToSend: number): Promise<void> => {
      if (!envelope || !draft) return;
      const patch = diffPatch(envelope.values, draft);
      if (Object.keys(patch).length === 0) {
        toast({
          title: t('useSettings.nothingToSaveTitle'),
          description: t('useSettings.nothingToSaveDesc'),
        });
        return;
      }
      setSaving(true);
      setError(null);
      try {
        const res = await patchSettings<TValues>(
          namespace,
          patch,
          versionToSend,
        );
        if (!mountedRef.current) return;
        setEnvelope((prev) =>
          prev
            ? {
                ...prev,
                values: res.values,
                version: res.version,
                updated_at: new Date().toISOString(),
              }
            : prev,
        );
        setDraftState(res.values);
        setConflict(null);
        toast({
          title: t('useSettings.savedTitle'),
          description: t('useSettings.savedDesc', { count: res.diff.length }),
        });
      } catch (err) {
        if (!mountedRef.current) return;
        if (isVersionConflict(err)) {
          // Open the conflict modal with both sides for diff.
          setConflict({
            open: true,
            yourValues: draft,
            serverValues: err.details.current_values as TValues,
            currentVersion: err.details.current_version,
            expectedVersion: err.details.expected_version,
          });
          // Also surface a toast so the modal isn't the only feedback for
          // users who momentarily missed it (e.g., scrolled away).
          toast({
            title: t('useSettings.conflictTitle'),
            description:
              err.message || t('useSettings.conflictDesc'),
            variant: 'error',
          });
        } else {
          setError(err instanceof Error ? err : new Error(String(err)));
          const msg =
            err instanceof ApiError
              ? err.message
              : t('useSettings.saveFailFallback');
          toast({
            title: t('useSettings.saveFailTitle'),
            description: msg,
            variant: 'error',
          });
        }
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    [envelope, draft, namespace, toast, t],
  );

  const save = useCallback(async (): Promise<void> => {
    if (!envelope) return;
    await runPatch(envelope.version);
  }, [envelope, runPatch]);

  const forceOverwrite = useCallback(async (): Promise<void> => {
    if (!conflict) return;
    // Replay PATCH with the server's current_version → BE accepts the patch
    // and bumps to current_version + 1 (audit log records the force per
    await runPatch(conflict.currentVersion);
  }, [conflict, runPatch]);

  const dismissConflict = useCallback((): void => {
    setConflict(null);
  }, []);

  return {
    loading,
    saving,
    envelope,
    draft,
    dirty,
    error,
    conflict,
    setDraft,
    reload,
    save,
    forceOverwrite,
    dismissConflict,
  };
}
